<template>
  <v-dialog
    v-model="dialog"
    persistent
    width="100%"
    content-class="voice-message-dialog-wrapper"
    overlay-class="voice-message-overlay"
  >
    <v-card class="py-5 px-2 relink-card">
      <v-btn
        icon
        small
        class="close-btn"
        @click="closeDialog"
      >
        <v-icon color="white" size="16">mdi-close</v-icon>
      </v-btn>

      <div class="popup-header text-center">
        <h2 class="font-weight-bold"><v-icon color="var(--v-caution-base)" size="28">mdi-alert-circle</v-icon>音声メッセージを再生します。</h2>
      </div>

      <div class="mx-6 my-5">
        <v-divider></v-divider>
      </div>

      <v-card-text class="text-center pa-0 pb-2">
        <div class="message-text mb-5">
          再生時に音声が流れます。<br>
          イヤフォンのご使用を推奨しております。<br>
          音声設定や周囲の環境をご確認の上、ご視聴ください。
        </div>

        <!-- 常にDOMに保持、srcはJSで設定（DOM属性に露出しない） -->
        <audio
          ref="audioEl"
          @timeupdate="onTimeUpdate"
          @loadedmetadata="onLoadedMetadata"
          @ended="onEnded"
          @error="onAudioError"
          style="display: none;"
        />

        <div v-if="isLoading" class="mb-4">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else-if="errorMessage" class="error-text mb-4">{{ errorMessage }}</div>

        <template v-else-if="audioReady">
          <div class="mb-3" style="color: #2C2B2B; font-size: 14px;">↓音声再生はこちらをタップ</div>
          <div class="audio-player mb-2 mx-2">
            <button class="audio-btn" @click="togglePlay">
              <v-icon size="20" color="#555">{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
            </button>
            <span class="audio-time">{{ formatTime(currentTime) }}</span>
            <input
              type="range"
              class="audio-seek"
              min="0"
              :max="duration || 0"
              :value="currentTime"
              :style="seekBarStyle"
              step="0.1"
              @input="onSeek"
            />
            <span class="audio-time">{{ formatTime(duration) }}</span>
            <button class="audio-btn" @click="toggleMute">
              <v-icon size="20" color="#555">{{ isMuted ? 'mdi-volume-off' : 'mdi-volume-high' }}</v-icon>
            </button>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { GET_MESSAGE_VOICE } from '@/store/message/actions'

export default {
  name: 'VoiceMessagePopup',
  props: {
    messageId: {
      type: [Number, String],
      required: true
    }
  },
  data () {
    return {
      dialog: false,
      isLoading: false,
      audioReady: false,
      errorMessage: null,
      isPlaying: false,
      isMuted: false,
      currentTime: 0,
      duration: 0
    }
  },
  computed: {
    seekBarStyle () {
      const pct = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0
      return {
        background: `linear-gradient(to right, #1a73e8 ${pct}%, #ccc ${pct}%)`
      }
    }
  },
  methods: {
    openDialog () {
      this.audioReady = false
      this.errorMessage = null
      this.isPlaying = false
      this.currentTime = 0
      this.duration = 0
      this.dialog = true
      this.fetchVoice()
    },
    closeDialog () {
      if (this.$refs.audioEl) {
        this.$refs.audioEl.pause()
        this.$refs.audioEl.src = ''
      }
      this.dialog = false
      this.isPlaying = false
      this.audioReady = false
      this.currentTime = 0
      this.duration = 0
      this.errorMessage = null
    },
    fetchVoice () {
      this.isLoading = true
      this.$store.dispatch(GET_MESSAGE_VOICE, { id: this.messageId })
        .then((data) => {
          this.$nextTick(() => {
            if (this.$refs.audioEl) {
              this.$refs.audioEl.src = data.url
              this.$refs.audioEl.load()
            }
          })
        })
        .catch((error) => {
          this.isLoading = false
          if (error.errorStatus === 410) {
            this.errorMessage = '再生期限が切れました'
          } else if (error.errorStatus === 404) {
            this.errorMessage = '音声メッセージが見つかりません'
          } else {
            this.errorMessage = '音声の取得に失敗しました'
          }
        })
    },
    onLoadedMetadata () {
      this.duration = this.$refs.audioEl.duration
      this.isLoading = false
      this.audioReady = true
    },
    onTimeUpdate () {
      this.currentTime = this.$refs.audioEl.currentTime
    },
    onEnded () {
      this.isPlaying = false
      this.currentTime = 0
      this.$refs.audioEl.currentTime = 0
    },
    onAudioError () {
      this.isPlaying = false
      this.audioReady = false
      this.isLoading = true
      this.$store.dispatch(GET_MESSAGE_VOICE, { id: this.messageId })
        .then((data) => {
          this.$nextTick(() => {
            if (this.$refs.audioEl) {
              this.$refs.audioEl.src = data.url
              this.$refs.audioEl.load()
            }
          })
        })
        .catch((error) => {
          this.isLoading = false
          if (error.errorStatus === 410) {
            this.errorMessage = '再生期限が切れました'
          } else {
            this.errorMessage = '音声の取得に失敗しました'
          }
        })
    },
    togglePlay () {
      if (this.isPlaying) {
        this.$refs.audioEl.pause()
        this.isPlaying = false
      } else {
        this.$refs.audioEl.play()
        this.isPlaying = true
      }
    },
    toggleMute () {
      this.isMuted = !this.isMuted
      this.$refs.audioEl.muted = this.isMuted
    },
    onSeek (e) {
      const time = parseFloat(e.target.value)
      this.$refs.audioEl.currentTime = time
      this.currentTime = time
    },
    formatTime (seconds) {
      if (!seconds || isNaN(seconds)) return '0:00'
      const m = Math.floor(seconds / 60)
      const s = Math.floor(seconds % 60)
      return `${m}:${s.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.relink-card {
  position: relative;
  overflow: visible !important;
  border-radius: 12px !important;
}
.close-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: var(--v-caution-base);
  z-index: 20;
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}
.popup-header h2 {
  color: #2C2B2B;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.message-text {
  color: #2C2B2B;
  font-size: 14px;
  line-height: 24px;
}
.error-text {
  color: #D32F2F;
  font-size: 14px;
}

/* カスタムオーディオプレイヤー */
.audio-player {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f3f4;
  border-radius: 100px;
  padding: 6px 10px;
}
.audio-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.audio-time {
  font-size: 11px;
  color: #555;
  white-space: nowrap;
  min-width: 28px;
  text-align: center;
  flex-shrink: 0;
}
.audio-seek {
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  height: 4px;
  border-radius: 2px;
  cursor: pointer;
  outline: none;
  min-width: 0;
}
.audio-seek::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #1a73e8;
  cursor: pointer;
}
.audio-seek::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #1a73e8;
  cursor: pointer;
  border: none;
}
</style>

<style>
.voice-message-dialog-wrapper,
.voice-message-dialog-wrapper * {
  overflow: visible !important;
}

.voice-message-dialog-wrapper .v-dialog {
  box-shadow: none !important;
}

.voice-message-dialog-wrapper .v-card.relink-card {
  box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
  border-radius: 12px !important;
}
</style>
